import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { SignResource } from 'src/app/services/logs/signs';
import { WorkFlowService } from 'src/app/components/workflow/workflow.service';
import { Document } from 'src/app/services/documents/documents';
import { Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import { WorkerVerificationService } from 'src/app/services/worker-verification/worker-verification.service';
import moment from 'moment';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { FormHistoryService, LogForm, LogSignature } from './form-history-services/form-history.service';
import { FormType } from 'src/app/common/enumerations/enumerations';
import { FormStatus } from 'src/app/services/forms/form-status';

@Component({
  selector: 'form-history',
  templateUrl: './form-history.component.html',
  styleUrls: ['./form-history.component.scss'],
})
export class FormHistoryComponent implements OnChanges, OnDestroy {

  @Input() form?: any;

  serialNo?: string;
  logForm: LogForm = {};
  signatures?: LogSignature[] = [];
  originalBody?: any;

  showBoxes?: boolean;
  showAll?: boolean = false;
  showLatestFirst?: boolean = true;

  document?: Document;
  documents?: Document[];
  documentsFiltered?: Document[];
  documentsSubs!: Subscription;

  formStatuses?: FormStatus[];
  formStatusesSubs!: Subscription;

  users?: User[];
  usersSubs!: Subscription;

  formType?: FormType;

  constructor(
    private wfService: WorkFlowService,
    private workerVerificationService: WorkerVerificationService,
    private store: Store,
    private service: FormHistoryService,
  ) { }

  ngOnDestroy(): void {
    this.documentsSubs?.unsubscribe();
    this.formStatusesSubs?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadUsers();
    this.loadDocuments();
    this.loadFormStatuses();
  }

  loadDocuments() {
    this.documentsSubs = this.store.select(state => state.Documents.data).subscribe(data => {
      if (data?.length) {
        this.documents = data;
        this.documentsFiltered = data;
        this.serialNo = this.form.serialNo ?? this.form.sequenceNumber.number;
        this.document = this.documents?.find(d => d.serialNo == this.serialNo);
        if (this.document)
          this.refresh();
      }
    });
  }

  loadUsers() {
    this.usersSubs = this.store.select(state => state.Users.data).subscribe(data => {
      if (data?.length) {
        this.users = data;
      }
    });
  }

  loadFormStatuses() {
    this.formStatusesSubs = this.store.select(state => state.FormStatuses.data).subscribe(data => {
      if (data?.length) {
        this.formStatuses = data;
      }
    });
  }

  refresh() {
    if (this.serialNo && this.document) {
      this.formType = this.document.type!;

      this.wfService.getLogsBySn(this.serialNo).subscribe(async response => {
        this.logForm = response;

        if (this.logForm.updates?.length)
          await this.processForm();
        this.computeWVSignatures();
        setTimeout(() => {
          this.refreshSignatures();
        }, 100);

      });
    }
  }

  refreshSignatures() {
    const signatures = this.logForm.signatures?.filter(s => s.formStatus).filter(s => this.showAll || this.isApprovalSignature(s)) ?? [];
    this.signatures = this.processSignatures(signatures);
  }

  /**
   * Keeps system-generated amendment activity out of the approval-only history while preserving it in the full activity view.
   * @param signature History entry returned by the workflow log service.
   * @returns Whether the entry should remain in the approval-only history.
   */
  private isApprovalSignature(signature: LogSignature): boolean {
    const name = signature.name?.trim() ?? '';
    return name !== 'Create' && name !== 'Update' &&
      name !== 'Update/FinishAmendment update' && !name.includes('Amendment Created');
  }

  /**
   * Sorts and decorates history activity, including persisted Minor Amendment indicators.
   * @param signatures History activity returned by the workflow log service.
   */
  processSignatures(signatures: LogSignature[]) {
    if (this.showLatestFirst)
      signatures = signatures
        .sort((a, b) => moment(a.signedOn).isBefore(b.signedOn) ? 1 : moment(a.signedOn).isAfter(b.signedOn) ? -1 : 0);
    else
      signatures = signatures
        .sort((a, b) => moment(a.signedOn).isAfter(b.signedOn) ? 1 : moment(a.signedOn).isBefore(b.signedOn) ? -1 : 0);

    signatures.forEach((signature, i) => {
      // Request logs identify Minor Amendments by name; workflow signatures use their amendment ID.
      const amendmentID = signature.amendmentId;
      signature.isMinorAmendment = this.formType == FormType.RSF && (
        signature.name?.includes('Minor Amendment') == true ||
        (!!amendmentID && amendmentID > 0 && this.form?.rsfAmendments?.some((amendment: any) =>
          amendment.isMinorAmendment && !!amendment.wfAmendmentID && amendment.wfAmendmentID == amendmentID) == true)
      );

      if (signature.name?.includes('Disapproved') && !signature.reason) {
        signature.name = signature.name.replace('Disapproved', 'Unsubmitted');
      }
      if (signature.formStatus) {
        const status = this.formStatuses?.find(s => s.id == signature.formStatus);
        signature.status = status?.name;
        signature.statusClass = status?.color;
      }
      signature.nameClass = signature.name?.includes('Approved') ? 'green' : signature.name?.includes('Disapproved') ? 'red' : signature.name?.includes('Unsubmitted') ? 'yellow' : signature.name?.includes('Amendment Created') ? 'orange' : 'blue';

      if (signature) {
        if (this.showLatestFirst) {
          signature.showStatus = signature.formStatus != signatures[i + 1]?.formStatus;
        }
        else {
          signature.showStatus = i > 0 && signature.formStatus != signatures[i - 1]?.formStatus || i == 0;
        }
      }

    });
    return signatures;
  }

  async processForm() {
    const originalBody = JSON.parse(this.logForm.originalBodyJson ?? '');
    if (originalBody) {
      const logForm = await this.service.processForm(originalBody, this.logForm, this.form);
      if (logForm)
        this.logForm = logForm;
      this.originalBody = this.logForm.originalBody;
    }
  }

  computeWVSignatures() {
    if (this.document?.type && this.document.formID) {
      this.workerVerificationService.getByForm(this.document.type, this.document.formID).toPromise().then(data => {
        for (const wv of data ?? []) {
          const wvSignature: SignResource = {
            name: 'Worker Verification',
            signedBy: wv.user?.name,
            signedOn: wv.date,
            amendmentId: 0,
          };
          this.logForm.signatures?.push(wvSignature);
        }
      });
    }
  }
}
